// frontend/src/components/CreateContactModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Leon du Plooy
// Updated:      2026-09-23 (global mobile create flow)
//
// Description:  Modal for adding a contact to a customer.
//               Uses makePortalUsername() utility (first3+first3+NNN).
//               Sends username, username_suggestion, portal_username.
//               Tries nested route then flat route; logs redacted payload.
//
// Mobile Responsiveness (2026-09-10):
//               • Preserves all contact payload, username-generation and endpoint behaviour.
//               • Identity, employment, email, phone, status/role and flag rows stack
//                 cleanly on phones while retaining the existing desktop grouping.
//               • Inputs, selects, checkboxes and the Create Contact action use touch-safe
//                 minimum sizes on mobile.
//               • Create Contact becomes full-width on phones and returns to its existing
//                 right-aligned compact presentation on larger screens.
//               • No duplicate mobile form tree is mounted.
//               • Global launches can select a customer when no customerId is
//                 supplied; existing customer-scoped launches remain unchanged.
//               • Successful creation can notify a global host through the
//                 optional onContactCreated callback.
//
// Operational Notes:
//               • Existing axios/token/header behaviour is intentionally unchanged in this
//                 responsive pass.
//               • Existing nested-route then flat-route fallback behaviour is unchanged.
//               • Existing portal username generation and redacted logging are unchanged.

import React, { useEffect, useMemo, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import axiosInstance from "../api/axiosInstance";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import { makePortalUsername } from "../utils/username";

const CreateContactModal = ({
  customerId = null,
  customers = [],
  onClose,
  onContactCreated = null,
}) => {
  const { triggerRefresh } = useDashboardRefresh();
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    customerId ? String(customerId) : ""
  );

  const [form, setForm] = useState({
    // Core
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    status: "Active",
    role: "Client",
    access_level: "",
    notes: "",
    portal_access: false,
    is_primary: false,

    // Extended
    title: "",
    department: "",
    job_title: "",
    secondary_email: "",
    mobile_number: "",
    direct_line: "",
    extension: "",
    whatsapp: "",
    preferred_contact_method: "",

    // Flags
    is_billing_contact: false,
    is_technical_contact: false,
  });

  const [existingUsernames, setExistingUsernames] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelectedCustomerId(customerId ? String(customerId) : "");
  }, [customerId]);

  const effectiveCustomerId = useMemo(() => {
    const value = customerId || selectedCustomerId;
    const numericId = Number(value);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }, [customerId, selectedCustomerId]);

  const customerOptions = useMemo(() => {
    const rows = Array.isArray(customers) ? customers : [];

    return rows
      .map((customer) => {
        const id = Number(customer?.id);
        const label = String(
          customer?.name
            || customer?.company_name
            || customer?.customer_name
            || customer?.client_name
            || (Number.isFinite(id) ? `Customer #${id}` : "Customer")
        ).trim();

        return { id, label };
      })
      .filter((customer) => Number.isFinite(customer.id) && customer.id > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  // Preload usernames to avoid collisions
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("anpr_token");
    axiosInstance
      .get("/users?limit=2000", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res.data) ? res.data : [];
        const set = new Set(arr.map((u) => String(u?.username || "").toLowerCase()).filter(Boolean));
        setExistingUsernames(set);
      })
      .catch((err) => {
        console.warn("⚠️ Failed to preload usernames:", err?.response?.data || err?.message || err);
        setExistingUsernames(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const tryPost = async (url, payload, headers) => {
    try {
      const res = await axiosInstance.post(url, payload, { headers });
      return res;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405 || status === 307 || status === 308) return null;
      throw err;
    }
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("anpr_token");
      if (!effectiveCustomerId) {
        alert("Customer ID missing.");
        setSubmitting(false);
        return;
      }

      const payload = { ...form, customer_id: effectiveCustomerId };

      if (payload.portal_access) {
        const unique = makePortalUsername(payload.first_name, payload.last_name, payload.email, existingUsernames);
        payload.username = unique;
        payload.username_suggestion = unique;
        payload.portal_username = unique;
        setExistingUsernames((prev) => new Set(prev).add(unique));
      }

      // Strip empty strings
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });

      // Redacted debug log
      const { email, secondary_email, phone_number, mobile_number, direct_line, ...dbg } = payload;
      console.info("➡️ CreateContact payload (redacted):", dbg);

      const headers = { Authorization: `Bearer ${token}` };

      // Nested first
      let res =
        (await tryPost(`/customers/${effectiveCustomerId}/contacts`, payload, headers)) ||
        (await tryPost(`/customers/${effectiveCustomerId}/contacts/`, payload, headers));

      // Flat fallback
      if (!res) {
        res =
          (await tryPost(`/contacts/`, payload, headers)) ||
          (await tryPost(`/contacts`, payload, headers));
      }

      if (!res) {
        const err = new Error(
          "No compatible create endpoint found. Tried /customers/{id}/contacts, /contacts/, /contacts."
        );
        err.code = "NO_CREATE_ENDPOINT";
        throw err;
      }

      const createdContact = res?.data?.contact || res?.data;
      alert("Contact created successfully");
      triggerRefresh();
      onContactCreated?.(createdContact);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error";
      console.error("Contact creation failed:", err?.response?.data || err?.message || err);
      alert(status ? `Failed to create contact (HTTP ${status}): ${detail}` : `Failed to create contact: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <h3 className="mb-4 break-words text-xl font-semibold">Add Contact</h3>

      <div className="flex min-w-0 flex-col gap-3">
        {!customerId && (
          <div className="flex min-w-0 flex-col gap-1">
            <label
              htmlFor="global-contact-customer"
              className="text-xs font-semibold text-gray-600"
            >
              Customer
            </label>
            <select
              id="global-contact-customer"
              value={selectedCustomerId}
              onChange={(event) => setSelectedCustomerId(event.target.value)}
              className="min-h-[44px] min-w-0 w-full rounded border bg-white p-2"
              required
            >
              <option value="">
                {customerOptions.length
                  ? "— Select customer —"
                  : "No customers available"}
              </option>
              {customerOptions.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Identity */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)]">
          <select
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
            className="min-h-[44px] w-full rounded border bg-white p-2 sm:w-40"
          >
            <option value="">Title</option>
            <option>Mr</option>
            <option>Ms</option>
            <option>Mrs</option>
            <option>Dr</option>
            <option>Prof</option>
          </select>

          <input
            type="text"
            value={form.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            placeholder="First Name"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
            required
          />
          <input
            type="text"
            value={form.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            placeholder="Last Name"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
            required
          />
        </div>

        {/* Employment */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="text"
            value={form.department}
            onChange={(e) => handleChange("department", e.target.value)}
            placeholder="Department"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
          <input
            type="text"
            value={form.job_title}
            onChange={(e) => handleChange("job_title", e.target.value)}
            placeholder="Job Title"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
        </div>

        {/* Emails */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="email"
            value={form.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="Email"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
            required
          />
          <input
            type="email"
            value={form.secondary_email}
            onChange={(e) => handleChange("secondary_email", e.target.value)}
            placeholder="Secondary Email"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
        </div>

        {/* Phones */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="tel"
            value={form.phone_number}
            onChange={(e) => handleChange("phone_number", e.target.value)}
            placeholder="Main Phone"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
          <input
            type="tel"
            value={form.mobile_number}
            onChange={(e) => handleChange("mobile_number", e.target.value)}
            placeholder="Mobile"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            type="tel"
            value={form.direct_line}
            onChange={(e) => handleChange("direct_line", e.target.value)}
            placeholder="Direct Line"
            className="min-h-[44px] min-w-0 w-full rounded border p-2"
          />
          <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
            <input
              type="text"
              value={form.extension}
              onChange={(e) => handleChange("extension", e.target.value)}
              placeholder="Ext."
              className="min-h-[44px] w-full rounded border p-2 sm:w-32"
            />
            <input
              type="text"
              value={form.whatsapp}
              onChange={(e) => handleChange("whatsapp", e.target.value)}
              placeholder="WhatsApp"
              className="min-h-[44px] min-w-0 w-full rounded border p-2"
            />
          </div>
        </div>

        {/* Preferences */}
        <div className="grid grid-cols-1 gap-3">
          <select
            value={form.preferred_contact_method}
            onChange={(e) => handleChange("preferred_contact_method", e.target.value)}
            className="min-h-[44px] min-w-0 w-full rounded border bg-white p-2"
          >
            <option value="">Preferred Contact Method</option>
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
            <option value="WhatsApp">WhatsApp</option>
          </select>
        </div>

        {/* Status / Role / Access */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select
            value={form.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className="min-h-[44px] min-w-0 w-full rounded border bg-white p-2"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          <select
            value={form.role}
            onChange={(e) => handleChange("role", e.target.value)}
            className="min-h-[44px] min-w-0 w-full rounded border bg-white p-2"
          >
            <option value="Primary">Primary</option>
            <option value="Admin">Admin</option>
            <option value="Finance">Finance</option>
            <option value="Other">Other</option>
            <option value="Client">Client</option>
          </select>
        </div>

        <select
          value={form.access_level}
          onChange={(e) => handleChange("access_level", e.target.value)}
          className="min-h-[44px] min-w-0 w-full rounded border bg-white p-2"
        >
          <option value="">Select Access Level</option>
          <option value="Organisation Manager">Organisation Manager</option>
          <option value="Client User Manager">Client User Manager</option>
          <option value="Job Viewer">Job Viewer</option>
          <option value="Portal Access Manager">Portal Access Manager</option>
          <option value="Brand Manager">Brand Manager</option>
        </select>

        {/* Flags */}
        <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-5">
          <label className="inline-flex min-h-[44px] touch-manipulation items-center">
            <input
              type="checkbox"
              checked={!!form.portal_access}
              onChange={(e) => handleChange("portal_access", e.target.checked)}
              className="mr-2 h-5 w-5 flex-none md:h-auto md:w-auto"
            />
            Portal Access
          </label>
          <label className="inline-flex min-h-[44px] touch-manipulation items-center">
            <input
              type="checkbox"
              checked={!!form.is_primary}
              onChange={(e) => handleChange("is_primary", e.target.checked)}
              className="mr-2 h-5 w-5 flex-none md:h-auto md:w-auto"
            />
            Primary Contact
          </label>
        </div>

        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-5">
          <label className="inline-flex min-h-[44px] touch-manipulation items-center">
            <input
              type="checkbox"
              checked={!!form.is_billing_contact}
              onChange={(e) => handleChange("is_billing_contact", e.target.checked)}
              className="mr-2 h-5 w-5 flex-none md:h-auto md:w-auto"
            />
            Billing Contact
          </label>
          <label className="inline-flex min-h-[44px] touch-manipulation items-center">
            <input
              type="checkbox"
              checked={!!form.is_technical_contact}
              onChange={(e) => handleChange("is_technical_contact", e.target.checked)}
              className="mr-2 h-5 w-5 flex-none md:h-auto md:w-auto"
            />
            Technical Contact
          </label>
        </div>
      </div>

      <div className="mt-5 sm:text-right">
        <button
          onClick={handleCreate}
          disabled={submitting || !effectiveCustomerId}
          className="min-h-[44px] w-full touch-manipulation rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow transition-colors duration-200 hover:bg-btnActionHover active:bg-btnActionClick disabled:opacity-60 sm:w-auto"
        >
          {submitting ? "Creating..." : "Create Contact"}
        </button>
      </div>
    </ModalWrapper>
  );
};

export default CreateContactModal;
